import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { BiArrowBack } from 'react-icons/bi';
import { platformAPI } from '../../../services/platformAPI';
import { AdminPickCell, AdminPlainBar, AdminRowDelete, useAdminRecordDelete } from '../../../components/workspace/useAdminRecordDelete';
import { useMasterRevision } from '../../../components/workspace/useMasterRevision';
import useDocumentTitle from '../../../hooks/useDocumentTitle';

const IssuersPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [label, setLabel] = useState('');
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  useDocumentTitle('Banks and issuers');

  const load = () => platformAPI.listSecurityIssuers()
    .then((response) => setRows(response.data?.data?.issuers || []))
    .catch((error) => setMessage({ variant: 'danger', text: error.response?.data?.message || 'Unable to load issuers' }))
    .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const admin = useAdminRecordDelete({
    entity: 'issuer',
    noun: 'issuer',
    plural: 'issuers',
    reload: load,
    label: (row) => row.label
  });
  const revision = useMasterRevision({ entity: 'issuer' });

  const save = async (event) => {
    event.preventDefault();
    try {
      await platformAPI.saveSecurityIssuer({ label });
      setLabel('');
      setMessage({ variant: 'success', text: 'Issuer saved. Inactive issuers stay readable on old instruments.' });
      await load();
    } catch (error) {
      setMessage({ variant: 'danger', text: error.response?.data?.message || 'Unable to save the issuer' });
    }
  };

  const setStatus = async (row, status) => {
    try {
      const changeReason = await revision.askReason(row.revision);
      if (!changeReason) return;
      await platformAPI.saveSecurityIssuer({ id: row._id, label: row.label, status, changeReason });
      await load();
    } catch (error) {
      setMessage({ variant: 'danger', text: error.response?.data?.message || 'Unable to update the issuer' });
    }
  };

  if (loading) return <Spinner animation="border" size="sm" />;
  return (
    <div className="cust-page">
      <button type="button" className="cust-page__back" onClick={() => navigate('/apps/tender/masters')}><BiArrowBack /> Masters</button>
      <h1 className="h4">Banks and issuers</h1>
      <p className="text-muted">Names used when a BG or EMD is recorded. The instrument form searches this list. A name already stored on an older instrument stays visible even if you later mark the issuer inactive.</p>
      {message ? <Alert variant={message.variant}>{message.text}</Alert> : null}
      {admin.notice}
      <form className="row g-2 mb-3" onSubmit={save}>
        <div className="col-md-6"><Form.Control placeholder="Bank or issuer name" value={label} onChange={(event) => setLabel(event.target.value)} required /></div>
        <div className="col-md-2"><Button size="sm" type="submit">Add</Button></div>
      </form>
      <AdminPlainBar admin={admin} rows={rows} />
      <Table size="sm">
        <thead><tr>{admin.enabled ? <th /> : null}<th>Name</th><th>Rev.</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row._id}>
              <AdminPickCell admin={admin} id={row._id} />
              <td>{row.label}</td>
              <td>{row.revision || 1}</td>
              <td>
                <span className={`cust-status cust-status--${row.status === 'inactive' ? 'inactive' : 'active'}`}>
                  {row.status === 'inactive' ? 'Inactive' : 'Active'}
                </span>
              </td>
              <td>
                {row.status === 'inactive'
                  ? <Button size="sm" variant="link" onClick={() => setStatus(row, 'active')}>Activate</Button>
                  : <Button size="sm" variant="link" onClick={() => setStatus(row, 'inactive')}>Deactivate</Button>}
                <Button size="sm" variant="link" onClick={() => revision.open(row)}>Revision info</Button>
                <AdminRowDelete admin={admin} id={row._id} label={row.label} />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {admin.modal}
      {revision.modals}
    </div>
  );
};

export default IssuersPage;
